import React, { useState, useContext, useMemo, useEffect } from 'react'
import { useAuthContext } from '../../../../Context/AuthContext/AuthContext'
import { AllStarContext } from '../../../../Context'
import { validateEmail } from '../../../../Components/Auth/authHelpers'
import { SaveBar } from '../../styles'
import { BlueBtn } from '../../../../Components/Buttons/styles'
import { useVoteStatus, useEmail } from './hooks'
import { EmailInput } from './styles'
import { logEvent } from '../../../../Utils'
import { ANALYTICS_TAGS } from '../../../../Constant'
import { getUserId } from '../../../../Utils'

export const SaveCon: React.FC<React.PropsWithChildren<unknown>> = () => {
  const authContext = useAuthContext()
  const { isLoggedIn, authLoading, login } = authContext
  const { saveAllPicks } = useContext(AllStarContext)!
  const { email, setEmail } = useEmail()
  const { canVote, setCanVote, isLoading: voteStatusLoading } = useVoteStatus()

  const [isSaving, setSaving] = useState<boolean>(false)
  const [errorMsg, setErrorMsg] = useState<string>('')
  const [success, setSuccess] = useState<boolean>(false)

  useEffect(() => {
    if (authLoading) return
  }, [isLoggedIn, authLoading])

  const handleEmailChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setEmail(event.target.value)
  }

  const isButtonActive = useMemo(
    () => email.includes('@') && !isSaving && canVote && !voteStatusLoading,
    [email, isSaving, canVote, voteStatusLoading]
  )

  const buttonText = useMemo(() => {
    if (success) return 'Success!'
    if (isSaving) return 'Saving...'
    if (voteStatusLoading) return 'Checking Status...'
    if (!canVote) return 'Locked'
    return 'Submit Vote'
  }, [success, isSaving, canVote, voteStatusLoading])

  const getButtonVariant = useMemo(() => {
    if (success) return 'success'
    if (!canVote && !voteStatusLoading) return 'locked'
    if (voteStatusLoading || isSaving) return 'loading'
    return 'default'
  }, [success, canVote, voteStatusLoading, isSaving])

  const handleSave = async () => {
    const userId = getUserId(authContext)

    if (!isButtonActive) {
      logEvent(ANALYTICS_TAGS.allstar_vote_attempt, {
        result: 'blocked',
        reason: !canVote ? 'locked' : 'invalid_email',
        email: email.includes('@') ? 'valid_format' : 'invalid_format',
        userId: userId || 'anonymous',
        isLoggedIn,
      })
      return
    }

    const { errorMsg: validationError } = validateEmail(email)
    if (validationError) {
      setErrorMsg(validationError)
      return
    }

    setErrorMsg('')
    setSaving(true)

    logEvent(ANALYTICS_TAGS.allstar_vote_attempt, {
      result: 'started',
      userId: userId || 'anonymous',
      isLoggedIn,
      email: 'valid',
    })

    try {
      const res = await saveAllPicks(email)
      if (res) {
        setSuccess(true)
        setCanVote(false)

        logEvent(ANALYTICS_TAGS.allstar_vote_success, {
          userId: userId || 'anonymous',
          isLoggedIn,
          voteTimestamp: new Date().toISOString(),
        })

        setTimeout(() => {
          setSuccess(false)
        }, 3000)
      } else {
        logEvent(ANALYTICS_TAGS.allstar_vote_failure, {
          reason: 'saveAllPicks_returned_false',
          userId: userId || 'anonymous',
          isLoggedIn,
        })
      }
    } catch (e) {
      setErrorMsg('An unexpected error occurred.')

      logEvent(ANALYTICS_TAGS.allstar_vote_failure, {
        reason: 'exception_thrown',
        error: e instanceof Error ? e.message : 'Unknown error',
        userId: userId || 'anonymous',
        isLoggedIn,
      })
    } finally {
      setSaving(false)
    }
  }

  const handleLoginClick = () => {
    login()
  }

  if (authLoading && !isLoggedIn)
    return (
      <SaveBar addMargin={isLoggedIn}>
        <div className="submitCon">
          <div className="subtextCon">
            <div className="subtext">Loading login status...</div>
          </div>
        </div>
      </SaveBar>
    )

  return (
    <SaveBar addMargin={isLoggedIn}>
      <div className="submitCon">
        <EmailInput
          required
          aria-label="email input"
          inputMode="email"
          label="Email"
          variant="outlined"
          margin="none"
          id="email"
          size="small"
          value={email}
          onChange={handleEmailChange}
          disabled={isSaving || voteStatusLoading}
        />
        <div className="error">{errorMsg}</div>
        <BlueBtn
          onClick={handleSave}
          disabled={!isButtonActive}
          data-variant={getButtonVariant}
          aria-label={buttonText}
        >
          {(isSaving || voteStatusLoading) && (
            <span className="loadingSpinner" aria-hidden="true">
              ⟳
            </span>
          )}
          {buttonText}
        </BlueBtn>
      </div>
      {!isLoggedIn && (
        <div className="subtextCon">
          <div className="subtext">{`-- Or `}</div>
          <button
            className="loginBtn"
            onClick={handleLoginClick}
            disabled={isSaving || voteStatusLoading}
          >
            login
          </button>
          <div className="subtext">to get PLL Nation credit. --</div>
        </div>
      )}
    </SaveBar>
  )
}
